import React, { useState, useEffect } from 'react';
import { Box, Typography, Container, Paper, Button, CircularProgress, Alert, Divider } from '@mui/material';
import api from '../services/api';
import projectService from '../services/projectService';
import authService from '../services/authService';

const ProjectDebug = () => {
  const [loading, setLoading] = useState(false);
  const [authStatus, setAuthStatus] = useState(null);
  const [apiResponse, setApiResponse] = useState(null);
  const [error, setError] = useState(null);
  const projectId = '67f0de2477adf33f0602b72d'; // The specific project ID you're trying to access
  
  // Check authentication status
  const checkAuth = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        setAuthStatus({
          isAuthenticated: false,
          message: 'No authentication token found in localStorage'
        });
        return;
      }
      
      // Try to validate the token
      try {
        const userData = await authService.validateToken();
        setAuthStatus({
          isAuthenticated: true,
          userId: userData.id,
          email: userData.email,
          role: userData.role,
          message: 'User is authenticated'
        });
      } catch (err) {
        setAuthStatus({
          isAuthenticated: false,
          tokenPresent: true,
          message: 'Token validation failed: ' + (err.message || 'Unknown error')
        });
      }
    } catch (err) {
      setError('Auth check error: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch project data directly using API
  const fetchProject = async () => {
    try {
      setLoading(true);
      
      // Make direct API call
      const response = await api.get(`/projects/${projectId}`);
      setApiResponse({
        status: response.status,
        statusText: response.statusText,
        data: response.data
      });
    } catch (err) {
      setApiResponse({
        error: true,
        status: err.response?.status,
        statusText: err.response?.statusText,
        message: err.message,
        data: err.response?.data
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Use project service
  const fetchProjectService = async () => {
    try {
      setLoading(true);
      const projectData = await projectService.getProjectById(projectId);
      setApiResponse({
        fromService: true,
        data: projectData
      });
    } catch (err) {
      setApiResponse({
        fromService: true,
        error: true,
        message: err.message,
      });
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    // Initial auth check on component mount
    checkAuth();
  }, []);

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 8 }}>
      <Typography variant="h4" gutterBottom>
        Project Debug Tool
      </Typography>
      
      <Typography variant="subtitle1" color="text.secondary" paragraph>
        This tool helps diagnose issues with accessing a specific project.
      </Typography>
      
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Target Project
        </Typography>
        <Typography variant="body1" sx={{ fontFamily: 'monospace', mb: 2 }}>
          ID: {projectId}
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={fetchProject}
            disabled={loading}
          >
            Test Direct API Call
          </Button>
          
          <Button 
            variant="outlined" 
            color="secondary" 
            onClick={fetchProjectService}
            disabled={loading}
          >
            Test Service Call
          </Button>
          
          <Button 
            variant="outlined" 
            onClick={checkAuth}
            disabled={loading}
          >
            Check Auth
          </Button>
        </Box>
        
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
      </Paper>
      
      {/* Auth Status */}
      {authStatus && (
        <Paper sx={{ p: 3, mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Authentication Status
          </Typography>
          
          <Alert 
            severity={authStatus.isAuthenticated ? "success" : "error"}
            sx={{ mb: 2 }}
          >
            {authStatus.message}
          </Alert>
          
          {authStatus.isAuthenticated && (
            <Box>
              <Typography variant="body2">
                <strong>User ID:</strong> {authStatus.userId}
              </Typography>
              <Typography variant="body2">
                <strong>Email:</strong> {authStatus.email}
              </Typography>
              <Typography variant="body2">
                <strong>Role:</strong> {authStatus.role}
              </Typography>
            </Box>
          )}
        </Paper>
      )}
      
      {/* API Response */}
      {apiResponse && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            {apiResponse.fromService ? 'Project Service Response' : 'API Response'}
          </Typography>
          
          {apiResponse.error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              Error: {apiResponse.message || 'Unknown error'}
              {apiResponse.status && ` (${apiResponse.status} ${apiResponse.statusText})`}
            </Alert>
          ) : (
            <Alert severity="success" sx={{ mb: 2 }}>
              Success: {apiResponse.status && `Status ${apiResponse.status} ${apiResponse.statusText}`}
            </Alert>
          )}
          
          <Divider sx={{ my: 2 }} />
          
          <Typography variant="subtitle2" gutterBottom>
            Response Data:
          </Typography>
          
          <Box 
            sx={{ 
              p: 2, 
              bgcolor: '#f5f5f5', 
              borderRadius: 1,
              maxHeight: 300,
              overflow: 'auto'
            }}
          >
            <pre style={{ margin: 0 }}>
              {JSON.stringify(apiResponse.data || {}, null, 2)}
            </pre>
          </Box>
        </Paper>
      )}
    </Container>
  );
};

export default ProjectDebug;